import React, { useEffect, useMemo, useState } from "react";

import type { OptionType, PickMainState } from "react-lib/apps/HISV3/common/CommonInterface";
import { formatISOtoBEDateTime } from "react-lib/utils/dateUtils";

import { AlertV4, ButtonV4, FieldV4, ModalV4, SelectV4, TabsV4 } from "../common/ui";

import { stageOf } from "../common/commonV4Interface";
import { optionText } from "../common/displayTextV4";
import { opdOADivisionOptions } from "../common/oaProvidersV4";
import { useOAProvidersV4 } from "../common/useOAProvidersV4";
import { BTN_ACTS as REG_BUTTONS } from "../common/sequence/RegistrationV4";
import {
  formatAppointmentRows,
  requestTerms,
  teamMessageOpeningData,
  walkInOpeningData,
  type AppointmentItem,
  type EncounterItem,
  type TeamMessageRecord,
  type WalkInOptions,
} from "./registrationModelV4";
import TeamMessageV4 from "./TeamMessageV4";
import "./_hisv4-registration.scss";

type EncounterTab = "appointment" | "walkin";

type CreateUpdateEncounterSlice = Partial<{
  division: number | string | null;
  doctor: number | string | null;
  encounterList: EncounterItem[];
  selectedEncounterItems: number[];
}> | null;

export type CardOpenEncounterV4Props = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "successMessage"
> & {
  setProp: (key: string, value: unknown) => void;
  runSequence: (params: Record<string, unknown>) => void;
  CreateUpdateEncounterSequence?: CreateUpdateEncounterSlice;
  appointmentEncounterList?: AppointmentItem[];
  masterOptions?: Partial<Record<"divisionOpd" | "doctor", OptionType[]>>;
  rehabAppointmentDoctors?: unknown[] | null;
  selectedAppointmentItems?: number[];
  userName?: string;
  apiToken?: string;
  divisionOptions?: OptionType[];
  selectedDivisionId?: number;
  onOpened?: () => void;
  patientId?: number;
  walkInOptions?: WalkInOptions;
};

const ENCOUNTER_SEQUENCE = "CreateUpdateEncounter";

// card เดียวกับ CardOpenEncounter: sequence เขียน error/success/buttonLoadCheck ด้วย key นี้
const ENCOUNTER_CARD = "CreateUpdateEncounter";

const OPEN_APPOINTMENT = "open_encounter_appointment";

const PRINT_SLIP = "print_visit_slip_form";

const SAVE_KEY = `${ENCOUNTER_CARD}_SAVE`;

const OPEN_APPOINTMENT_KEY = `${ENCOUNTER_CARD}_${OPEN_APPOINTMENT}`;

const PRINT_SLIP_KEY = `${ENCOUNTER_CARD}_${PRINT_SLIP}`;

/** เปิด Encounter ของผู้รับบริการ: มาตามนัด / Walk-in + Encounter ของผู้รับบริการ */
const CardOpenEncounterV4 = (props: CardOpenEncounterV4Props) => {
  const [tab, setTab] = useState<EncounterTab>("walkin");
  const [opened, setOpened] = useState(false);
  const [restarted, setRestarted] = useState(false);
  const [arriveChoice, setArriveChoice] = useState<{ patientId?: number; code: string } | null>(null);
  const [chiefComplaint, setChiefComplaint] = useState("");
  const [channel, setChannel] = useState("");
  const [requestEdit, setRequestEdit] = useState<{
    patientId?: number; values: string[]; baseline: TeamMessageRecord;
  } | null>(null);
  const [refreshFrom, setRefreshFrom] = useState<WalkInOptions | null>(null);
  const [cancelRow, setCancelRow] = useState<EncounterItem | null>(null);
  const [reason, setReason] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [canceled, setCanceled] = useState(false);

  const seq = props.CreateUpdateEncounterSequence;
  const divisions = opdOADivisionOptions(props.divisionOptions, props.masterOptions?.divisionOpd);
  const validDivision = divisions.some((option) => Number(option.value ?? option.id) === Number(seq?.division));
  const providers = useOAProvidersV4(props.apiToken, validDivision ? Number(seq?.division) : undefined);
  const doctors = providers.doctors.map((doctor) => ({ value: doctor.id, text: doctor.name_code || doctor.full_name || "" }));
  const validDoctor = doctors.some((doctor) => doctor.value === Number(seq?.doctor));

  const selectedAppointments = props.selectedAppointmentItems ?? [];

  const rawError = props.errorMessage?.[ENCOUNTER_CARD];

  // error ที่มีรายการ detail = ต้องยืนยันก่อนเปิดจากนัด
  const confirmError =
    Array.isArray(rawError?.detail) && typeof rawError?.error === "string" ? rawError : null;

  const error = confirmError ? null : rawError;

  // ยังไม่โหลดของผู้ป่วยคนนี้: restart ล้าง encounterList, นัดมาใน setState เดียวกัน
  const pending = !restarted || (seq?.encounterList === undefined && !rawError);

  // Walk-in: ค่ามาโดย / Request พิเศษล่าสุดของผู้รับบริการ
  const walkInOptions = props.walkInOptions?.patientId === props.patientId
    && props.walkInOptions?.divisionId === props.selectedDivisionId ? props.walkInOptions : undefined;
  const chosenArrive = arriveChoice && arriveChoice.patientId === props.patientId ? arriveChoice.code : "WLK";
  const arrive = walkInOptions?.arriveLoaded && props.buttonLoadCheck?.[REG_BUTTONS.LOAD_WALKIN] !== "LOADING"
    && walkInOptions.arrive.some((term) => term.code === chosenArrive) ? chosenArrive : "";
  const edited = requestEdit?.patientId === props.patientId ? requestEdit : null;
  const baseline = edited?.baseline ?? walkInOptions?.specialRequests;
  const requests = edited?.values ?? (baseline?.items ?? []).map((item) => String(item.id));
  const requestsDisabled = !walkInOptions?.specialRequests
    || props.buttonLoadCheck?.[REG_BUTTONS.LOAD_WALKIN] === "LOADING"
    || walkInOptions.requestsLoaded === false
    || (!!refreshFrom && refreshFrom === walkInOptions);
  const teamOptions = walkInOptions ? { ...walkInOptions, requests: requestTerms(walkInOptions, baseline) } : undefined;

  // Memo
  const appointments = useMemo(
    () => (pending ? [] : formatAppointmentRows(props.appointmentEncounterList ?? [])),
    [pending, props.appointmentEncounterList],
  );

  const encounters = useMemo(
    () => (pending ? [] : (seq?.encounterList ?? []).filter((item) => item.status !== "CANCELED")),
    [pending, seq?.encounterList],
  );

  const sendTo = useMemo(() => {
    if (tab === "appointment") {
      const firstAppointment = appointments.find((row) => selectedAppointments.includes(row.id));

      return [
        firstAppointment?.division || "ยังไม่เลือกนัด",
        firstAppointment?.doctor || "ยังไม่เลือกผู้ให้บริการ",
      ];
    }

    return [
      optionText(divisions, seq?.division) || "ยังไม่เลือกแผนก",
      optionText(doctors, seq?.doctor) || "ยังไม่เลือกผู้ให้บริการ",
    ];
  }, [tab, appointments, selectedAppointments, props.divisionOptions, providers.doctors, seq?.division, seq?.doctor]);

  // Effect
  useEffect(() => {
    setRequestEdit(null);
    setRefreshFrom(null);
    setArriveChoice(null);
    setChiefComplaint("");
    setChannel("");
  }, [props.patientId]);

  useEffect(() => {
    loadWalkIn();
  }, [props.patientId, props.selectedDivisionId]);

  useEffect(() => {
    if (!props.successMessage?.[`${ENCOUNTER_CARD}_CANCEL`]) return;

    props.setProp(`successMessage.${ENCOUNTER_CARD}_CANCEL`, null);
    closeCancel();
    setCanceled(true);
    setRequestEdit(null);
    setRefreshFrom(walkInOptions ?? null);
    loadWalkIn();
  }, [props.successMessage?.[`${ENCOUNTER_CARD}_CANCEL`]]);

  useEffect(() => {
    if (!seq || validDivision || divisions.length === 0) return;
    const initial = divisions.find((option) => Number(option.value ?? option.id) === props.selectedDivisionId);
    props.setProp("CreateUpdateEncounterSequence.division", initial ? Number(initial.value ?? initial.id) : "");
    props.setProp("CreateUpdateEncounterSequence.doctor", "");
  }, [!!seq, seq?.division, validDivision, props.selectedDivisionId, props.divisionOptions, props.masterOptions?.divisionOpd]);

  useEffect(() => {
    if (seq?.doctor && !providers.loading && !validDoctor) {
      props.setProp("CreateUpdateEncounterSequence.doctor", "");
    }
  }, [seq?.doctor, providers.loading, validDoctor]);

  useEffect(() => {
    props.runSequence({
      sequence: ENCOUNTER_SEQUENCE,
      card: ENCOUNTER_CARD,
      restart: true,
      isNoneFilterDate: true,
    });

    setRestarted(true);

    return () => {
      props.setProp(`successMessage.${ENCOUNTER_CARD}`, null);
      props.setProp(`errorMessage.${ENCOUNTER_CARD}`, null);
      props.setProp("CreateUpdateEncounterSequence.selectedEncounterItems", []);
      props.setProp("CreateUpdateEncounterSequence.walkInOpening", undefined);
      props.setProp(`successMessage.${ENCOUNTER_CARD}_CANCEL`, null);
      props.setProp(`errorMessage.${ENCOUNTER_CARD}_CANCEL`, null);
    };
  }, []);

  // มีนัดของผู้ป่วยแล้ว เปิดที่แท็บนัดก่อน
  useEffect(() => {
    if (appointments.length > 0) {
      setTab("appointment");
    }
  }, [appointments.length > 0]);

  useEffect(() => {
    if (!props.successMessage?.[ENCOUNTER_CARD]) {
      return;
    }

    props.setProp(`successMessage.${ENCOUNTER_CARD}`, null);

    setOpened(true);
    setArriveChoice(null); setChiefComplaint(""); setRequestEdit(null); setChannel("");
    setRefreshFrom(walkInOptions ?? null);
    loadWalkIn();
    props.onOpened?.();
  }, [props.successMessage?.[ENCOUNTER_CARD]]);

  // Handler
  const loadWalkIn = () => {
    if (props.patientId) props.runSequence({ sequence: "RegistrationV4", action: "LOAD_WALKIN", patientId: props.patientId });
  };

  const closeCancel = () => {
    setCancelRow(null);
    setReason("");
    setUsername("");
    setPassword("");
    props.setProp(`errorMessage.${ENCOUNTER_CARD}_CANCEL`, null);
  };

  const handleChangeArrive = (code: string) => {
    setArriveChoice({ patientId: props.patientId, code });
  };

  const handleChangeRequests = (values: string[]) => {
    if (!requestsDisabled && baseline) setRequestEdit({ patientId: props.patientId, values, baseline });
  };

  const handleOpenCancel = (event: React.MouseEvent, row: EncounterItem) => {
    event.stopPropagation();
    setCancelRow(row);
    setCanceled(false);
    props.setProp(`errorMessage.${ENCOUNTER_CARD}_CANCEL`, null);
  };

  const handleSubmitCancel = (event: React.FormEvent) => {
    event.preventDefault();
    if (!cancelRow || !reason.trim() || !username.trim() || !password) return;
    props.setProp("CreateUpdateEncounterSequence.selectedEncounter", cancelRow);
    props.runSequence({
      sequence: ENCOUNTER_SEQUENCE,
      card: ENCOUNTER_CARD,
      action: "delete",
      isV4Cancellation: true,
      encounterId: cancelRow.id,
      buttonLoadKey: `${ENCOUNTER_CARD}_CANCEL`,
      getCredentials: () => ({ note: reason.trim(), username: username.trim(), password }),
    });
  };

  const handleToggleAppointment = (id: number) => {
    props.setProp(
      "selectedAppointmentItems",
      selectedAppointments.includes(id)
        ? selectedAppointments.filter((item) => item !== id)
        : [...selectedAppointments, id],
    );
  };

  const handleOpenAppointment = (confirmed?: boolean) => {
    const teamMessage = teamMessageOpeningData(channel, requests,
      walkInOptions ? { ...walkInOptions,
        specialRequests: requestsDisabled ? undefined : walkInOptions.specialRequests } : undefined,
      baseline);
    props.runSequence({
      sequence: ENCOUNTER_SEQUENCE,
      action: OPEN_APPOINTMENT,
      card: ENCOUNTER_CARD,
      confirmed,
      teamMessage,
    });
  };

  const handleCreate = () => {
    if (!validDivision || !validDoctor || providers.loading || providers.error) return;
    setOpened(false);
    const opening = walkInOpeningData(arrive, chiefComplaint, channel, requests,
      walkInOptions ? { ...walkInOptions, arriveLoaded: !!arrive,
        specialRequests: requestsDisabled ? undefined : walkInOptions.specialRequests } : undefined,
      baseline);
    props.setProp("CreateUpdateEncounterSequence.walkInOpening", opening);
    props.runSequence({
      sequence: ENCOUNTER_SEQUENCE,
      action: "create",
      card: ENCOUNTER_CARD,
      buttonLoadKey: SAVE_KEY,
      // Also pass the same value to avoid a React setState batching race.
      walkInOpening: opening,
    });
  };

  const handlePrint = () => {
    props.runSequence({ sequence: ENCOUNTER_SEQUENCE, action: PRINT_SLIP, card: ENCOUNTER_CARD });
  };

  const handleSelectEncounter = (id: number) => {
    props.setProp("CreateUpdateEncounterSequence.selectedEncounterItems", [id]);
  };

  const handleChangeDivision = (value: string) => {
    props.setProp("CreateUpdateEncounterSequence.doctor", "");
    props.setProp("CreateUpdateEncounterSequence.division", value === "" ? "" : Number(value));
  };

  const handleChangeDoctor = (value: string) => {
    props.setProp("CreateUpdateEncounterSequence.doctor", value === "" ? "" : Number(value));
  };

  const handleConfirmOpen = () => {
    handleCloseError();
    handleOpenAppointment(true);
  };

  const handleCloseOpened = () => {
    setOpened(false);
  };

  const handleCloseRehab = () => {
    props.setProp("rehabAppointmentDoctors", null);
  };

  const handleCloseError = () => {
    props.setProp(`errorMessage.${ENCOUNTER_CARD}`, null);
  };

  // Render
  const renderEmptyRow = (colSpan: number, text: string) => (
    <tr>
      <td colSpan={colSpan} className="hisv4-table-empty">
        {pending ? "กำลังโหลด" : text}
      </td>
    </tr>
  );

  const renderAppointmentTab = () => (
    <section className="hv4-card">
      <h2 className="hv4-card-title">นัดหมายของผู้รับบริการ</h2>
      <p className="hv4-reg-oe-sub">
        เลือกนัดที่ผู้รับบริการมาตามนัดวันนี้ ระบบดึงผู้ให้บริการ แผนก และรายละเอียดการนัดให้เอง
      </p>
      <div className="hv4-reg-oe-table">
        <table className="hisv4-table">
          <thead>
            <tr>
              <th aria-label="เลือก" />
              <th>วันที่นัด</th>
              <th>เวลานัด</th>
              <th>ผู้ให้บริการ</th>
              <th>แผนก</th>
              <th>Orders</th>
              <th>รายละเอียดการนัด</th>
            </tr>
          </thead>
          <tbody>
            {appointments.length === 0
              ? renderEmptyRow(7, "ไม่มีนัดหมายที่เปิด Encounter ได้")
              : appointments.map((row) => (
                  <tr
                    key={row.id}
                    className={selectedAppointments.includes(row.id) ? "hv4-reg-oe-row--on" : ""}
                    onClick={() => handleToggleAppointment(row.id)}
                  >
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`เลือกนัด ${row.date} ${row.time}`}
                        checked={selectedAppointments.includes(row.id)}
                        onChange={() => handleToggleAppointment(row.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>{row.date || "-"}</td>
                    <td>{row.time || "-"}</td>
                    <td>{row.doctor || "-"}</td>
                    <td>{row.division || "-"}</td>
                    <td>{row.orders || "-"}</td>
                    <td>{row.detail || "-"}</td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderWalkinTab = () => (
    <>
      <section className="hv4-card">
        <h2 className="hv4-card-title">เปิด Encounter ใหม่ (ไม่มีนัด)</h2>
        <p className="hv4-reg-oe-sub">เลือกแผนกและผู้ให้บริการ แล้วเปิดได้เลย</p>
        {providers.error && <AlertV4 tone="danger">{providers.error} <button onClick={providers.reload}>ลองใหม่</button></AlertV4>}
        <div className="hv4-reg-oe-form">
          <FieldV4 label="แผนก *" htmlFor="hv4-reg-oe-division">
            <SelectV4
              id="hv4-reg-oe-division"
              search
              placeholder="เลือกแผนก"
              options={divisions}
              value={String(seq?.division ?? "")}
              onChange={handleChangeDivision}
            />
          </FieldV4>
          <FieldV4 label="ผู้ให้บริการ *" htmlFor="hv4-reg-oe-doctor">
            <SelectV4
              id="hv4-reg-oe-doctor"
              search
              placeholder="เลือกผู้ให้บริการ"
              options={doctors}
              disabled={!validDivision || providers.loading || !!providers.error}
              value={validDoctor ? String(seq?.doctor) : ""}
              onChange={handleChangeDoctor}
            />
          </FieldV4>
          <FieldV4 label="มาโดย" htmlFor="hv4-reg-arrive">
            <SelectV4
              id="hv4-reg-arrive"
              placeholder="ยังไม่ได้เลือก"
              options={[
                { value: "", text: "ยังไม่ได้เลือก" },
                ...(walkInOptions?.arrive ?? [])
                  .filter((item) => item.code)
                  .map((item) => ({ value: item.code!, text: item.name })),
              ]}
              value={arrive}
              onChange={handleChangeArrive}
            />
          </FieldV4>
          <FieldV4 className="hv4-reg-oe-wide" label="อาการนำ (ไม่บังคับ — คัดกรองบันทึกเพิ่มได้)" htmlFor="hv4-reg-chief">
            <input
              id="hv4-reg-chief"
              className="hv4-input"
              maxLength={255}
              value={chiefComplaint}
              onChange={(e) => setChiefComplaint(e.target.value)}
            />
          </FieldV4>
        </div>
      </section>
    </>
  );

  const renderTeamMessage = () => (
    <>
      <AlertV4 tone="warn" error={props.errorMessage?.[REG_BUTTONS.LOAD_WALKIN]} />
      {props.errorMessage?.[REG_BUTTONS.LOAD_WALKIN] && (
        <ButtonV4 className="hv4-reg-oe-retry" variant="ghost" onClick={loadWalkIn}>
          ลองใหม่
        </ButtonV4>
      )}
      <TeamMessageV4
        options={teamOptions}
        channel={channel}
        requests={requests}
        requestsDisabled={requestsDisabled}
        onChannel={setChannel}
        onRequests={handleChangeRequests}
      />
    </>
  );

  const renderBar = () => (
    <div className="hv4-reg-bar">
      <span className="hv4-reg-bar-text">
        ส่งไป: {sendTo.join(" · ")}
        {tab === "walkin" ? " · สถานะเริ่มต้น: รอคัดกรอง" : ""}
        {props.userName ? ` · บันทึกโดย ${props.userName}` : ""}
      </span>
      {tab === "appointment" ? (
        <ButtonV4
          className="hv4-reg-btn-encounter"
          disabled={
            selectedAppointments.length === 0 ||
            props.buttonLoadCheck?.[OPEN_APPOINTMENT_KEY] === "LOADING"
          }
          onClick={() => handleOpenAppointment()}
        >
          ลงทะเบียนจากนัดหมาย (เปิด Encounter)
        </ButtonV4>
      ) : (
        <ButtonV4
          className="hv4-reg-btn-encounter"
          disabled={
              !validDivision || !validDoctor || providers.loading || !!providers.error || props.buttonLoadCheck?.[SAVE_KEY] === "LOADING"
          }
          onClick={handleCreate}
        >
          เปิด Encounter
        </ButtonV4>
      )}
    </div>
  );

  const renderEncounters = () => (
    <section className="hv4-card">
      <div className="hv4-reg-oe-enc-head">
        <h2 className="hv4-card-title">Encounter ของผู้รับบริการ</h2>
        <ButtonV4
          variant="ghost"
          disabled={
            !seq?.selectedEncounterItems?.length ||
            props.buttonLoadCheck?.[PRINT_SLIP_KEY] === "LOADING"
          }
          onClick={handlePrint}
        >
          พิมพ์บัตรเข้ารับการรักษา
        </ButtonV4>
      </div>
      <div className="hv4-reg-oe-table">
        <table className="hisv4-table">
          <thead>
            <tr>
              <th>Encounter</th>
              <th>แผนก</th>
              <th>ผู้ให้บริการ</th>
              <th>สถานะ</th>
              <th>เริ่มเมื่อ</th>
              <th>เปิดโดย</th>
              <th aria-label="การดำเนินการ" />
            </tr>
          </thead>
          <tbody>
            {encounters.length === 0
              ? renderEmptyRow(7, "ยังไม่มี Encounter")
              : encounters.map((row) => {
                  const on = !!seq?.selectedEncounterItems?.includes(row.id);
                  return (
                    <tr
                      key={row.id}
                      aria-selected={on}
                      className={on ? "hv4-reg-oe-row--on" : ""}
                      onClick={() => handleSelectEncounter(row.id)}
                    >
                      <td className="hv4-reg-oe-no">{row.number || "-"}</td>
                      <td>{row.division_name || "-"}</td>
                      <td>{row.doctor_name || "-"}</td>
                      <td>
                        <span
                          className={`hv4-enc-status ${stageOf({ status: row.status })?.key ?? ""}`}
                        >
                          {row.status_name || "-"}
                        </span>
                      </td>
                      <td>
                        {formatISOtoBEDateTime(row.started || row.created).dateTimeShort || "-"}
                      </td>
                      <td>{row.opened_by_name || "-"}</td>
                      <td>
                        {row.status === "WAIT_TRIAGE" && (
                          <ButtonV4 variant="danger-ghost" size="sm" onClick={(event) => handleOpenCancel(event, row)}>
                            ยกเลิก
                          </ButtonV4>
                        )}
                      </td>
                    </tr>
                  );
                })}
          </tbody>
        </table>
      </div>
    </section>
  );

  const renderCancelModal = (row: EncounterItem) => (
    <ModalV4 title={`ยกเลิก Encounter ${row.number || row.id}`} width={480} onClose={closeCancel}>
      <form onSubmit={handleSubmitCancel}>
        <div className="hv4-reg-modal-body">
          <FieldV4 label="เหตุผล *" htmlFor="hv4-cancel-reason">
            <input id="hv4-cancel-reason" className="hv4-input" required value={reason} onChange={(e) => setReason(e.target.value)} />
          </FieldV4>
          <FieldV4 label="Username *" htmlFor="hv4-cancel-username">
            <input id="hv4-cancel-username" className="hv4-input" autoComplete="off" required value={username} onChange={(e) => setUsername(e.target.value)} />
          </FieldV4>
          <FieldV4 label="Password *" htmlFor="hv4-cancel-password">
            <input id="hv4-cancel-password" className="hv4-input" autoComplete="new-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </FieldV4>
          <AlertV4 tone="danger" error={props.errorMessage?.[`${ENCOUNTER_CARD}_CANCEL`]} />
        </div>
        <div className="hv4-reg-modal-foot">
          <ButtonV4 variant="ghost" onClick={closeCancel}>
            ปิด
          </ButtonV4>
          <ButtonV4
            variant="danger"
            type="submit"
            disabled={!reason.trim() || !username.trim() || !password || props.buttonLoadCheck?.[`${ENCOUNTER_CARD}_CANCEL`] === "LOADING"}
          >
            ยืนยันยกเลิก Encounter
          </ButtonV4>
        </div>
      </form>
    </ModalV4>
  );

  const renderConfirmModal = () => (
    <ModalV4 title="ยืนยันเปิด Encounter" width={480} onClose={handleCloseError}>
      <div className="hv4-reg-modal-body">
        <strong>{confirmError.error}</strong>
        <ul>
          {confirmError.detail.map((item: { name?: string }, index: number) => (
            <li key={index}>{item.name}</li>
          ))}
        </ul>
      </div>
      <div className="hv4-reg-modal-foot">
        <ButtonV4 variant="ghost" onClick={handleCloseError}>
          ยกเลิก
        </ButtonV4>
        <ButtonV4 variant="ok" onClick={handleConfirmOpen}>
          ยืนยัน
        </ButtonV4>
      </div>
    </ModalV4>
  );

  return (
    <div className="hv4-reg-oe">
      <div className="hv4-page-head">
        <h1 className="hv4-h1">เปิด Encounter</h1>
        <span className="hv4-page-sub">เลือกว่าผู้ป่วยมาตามนัด หรือมาโดยไม่มีนัด (Walk-in)</span>
      </div>

      <TabsV4<EncounterTab>
        ariaLabel="ประเภทการมา"
        items={[
          { key: "appointment", label: `มีนัดหมาย (${appointments.length})` },
          { key: "walkin", label: "ไม่มีนัด (Walk-in)" },
        ]}
        active={tab}
        onChange={setTab}
      />

      {opened && (
        <AlertV4 tone="info" onClose={handleCloseOpened}>
          เปิด Encounter แล้ว
        </AlertV4>
      )}

      {canceled && <AlertV4 tone="info" onClose={() => setCanceled(false)}>ยกเลิก Encounter แล้ว</AlertV4>}
      <AlertV4 tone="danger" error={error} onClose={handleCloseError} />

      {props.rehabAppointmentDoctors?.length ? (
        <AlertV4 tone="warn" onClose={handleCloseRehab}>
          นัดกายภาพบำบัดที่เลือกยังไม่ระบุแพทย์ จึงเปิด Encounter ไม่ได้
        </AlertV4>
      ) : null}

      {tab === "appointment" ? renderAppointmentTab() : renderWalkinTab()}
      {renderTeamMessage()}

      {renderBar()}

      {renderEncounters()}

      {cancelRow && renderCancelModal(cancelRow)}

      {confirmError && renderConfirmModal()}
    </div>
  );
};

CardOpenEncounterV4.displayName = "CardOpenEncounterV4";

export default CardOpenEncounterV4;
